import { useCartAndDeliveryDatesDataContext } from '@app/data/clients/cartCheckoutClient';
import { Costs, CostsProps } from '../Costs';
import { transformMonolithCartToCostProps } from './utils';

type MonolithCartCostsContainerProps = Pick<
  CostsProps,
  | 'pendingTaxText'
  | 'isShippingEstimated'
  | 'includeShippingTaxWithShippingAmount'
  | 'showSubtotalOnly'
>;

export const MonolithCartCostsContainer = ({
  pendingTaxText,
  isShippingEstimated,
  includeShippingTaxWithShippingAmount,
  showSubtotalOnly,
}: MonolithCartCostsContainerProps) => {
  const { cart, cartLoading } = useCartAndDeliveryDatesDataContext();

  if (!cart || cartLoading) {
    return <Costs.Loading />;
  }

  const props = transformMonolithCartToCostProps(cart);

  return (
    <Costs
      currency={props.currency}
      lineItemsCount={props.lineItemsCount}
      total={props.total}
      undiscountedSubtotal={props.undiscountedSubtotal}
      subtotal={props.subtotal}
      shippingMethod={props.shippingMethod}
      shippingAmountExcludingTax={props.shippingAmountExcludingTax}
      shippingAmountWithTax={props.shippingAmountWithTax}
      shippingEstimateTaxAmount={props.shippingEstimateTaxAmount}
      taxAmount={props.taxAmount}
      showTaxSeparately={props.showTaxSeparately}
      flatRateApplied={props.flatRateApplied}
      digitalProductsOnly={props.digitalProductsOnly}
      appliedCoupon={props.appliedCoupon}
      /**
       * This is relevant to when user chooses to pay with Apple/Google/PayPal from the cart page.
       * Initially, the cart does not include any tax. Once the user selects one of these payment methods, we retrieve the cart details with the calculated tax amounts.
       * In countries where tax is displayed separately (e.g., the US), we present the total tax (including both shipping and product costs) as a distinct row.
       * However, in the modals for the mentioned payment methods, the shipping amount is shown inclusive of tax.
       * By passing `includeShippingTaxWithShippingAmount` we ensure consistency between amount breakdowns on the cart page and the modals -
       * we adjust the displayed amounts on the page by deducting the shipping tax from the tax row and adding it to the shipping amount.
       */
      includeShippingTaxWithShippingAmount={
        includeShippingTaxWithShippingAmount
      }
      pendingTaxText={pendingTaxText}
      isTaxComputed={props.isTaxComputed}
      isShippingEstimated={isShippingEstimated}
      showSubtotalOnly={showSubtotalOnly}
    />
  );
};
